import React, { useEffect, useRef, useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import CustomCursor from './CustomCursor';


function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600;700&display=swap');

  .ipulse-root {
    font-family: 'Outfit', sans-serif;
    --purple:       #6C3FC5;
    --purple-light: #8B5CF6;
    --purple-dark:  #2D1B69;
    --purple-deep:  #120B2E;
    --gold:         #C9A84C;
    --cream:        #F8F5FF;
    background: var(--cream);
  }
  .ipulse-root *, .ipulse-root *::before, .ipulse-root *::after {
    box-sizing: border-box;
    font-family: 'Outfit', sans-serif;
  }
  .ipulse-root h1, .ipulse-root h2, .ipulse-root h3,
  .ipulse-root h4, .ipulse-root h5, .ipulse-root h6 {
    font-family: 'Cormorant Garamond', serif;
  }

  /* ── HERO ── */
  .hero-section {
    position: relative; overflow: hidden;
    background: linear-gradient(135deg, #120B2E 0%, #2D1B69 55%, #1A0D3D 100%);
    min-height: 88vh;
    display: flex; align-items: center; justify-content: center;
  }

  .hero-grid { position: absolute; inset: 0; pointer-events: none; perspective: 600px; }
  .hero-grid-inner {
    position: absolute; bottom: -40px; left: -20%; right: -20%; height: 60%;
    background-image:
      linear-gradient(rgba(139,92,246,0.12) 1px, transparent 1px),
      linear-gradient(90deg, rgba(139,92,246,0.12) 1px, transparent 1px);
    background-size: 60px 60px;
    transform: rotateX(70deg); transform-origin: bottom center;
    animation: grid-scroll 8s linear infinite;
    mask-image: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 80%);
  }
  @keyframes grid-scroll { from { background-position: 0 0; } to { background-position: 0 60px; } }

  .hero-orbit {
    position: absolute; inset: -48px; border-radius: 50%;
    border: 1px solid rgba(139,92,246,0.25);
    animation: orbit3d 8s linear infinite; transform-style: preserve-3d;
  }
  .hero-orbit-2 { inset: -72px; border-color: rgba(201,168,76,0.15); animation: orbit3d 12s linear infinite reverse; }
  @keyframes orbit3d {
    0%   { transform: rotateX(70deg) rotateZ(0deg); }
    100% { transform: rotateX(70deg) rotateZ(360deg); }
  }
  .hero-orbit::after {
    content: ''; position: absolute; top: -4px; left: 50%;
    width: 8px; height: 8px; background: var(--purple-light); border-radius: 50%;
    box-shadow: 0 0 12px 3px rgba(139,92,246,0.8); transform: translateX(-50%);
  }
  .hero-orbit-2::after { background: var(--gold); box-shadow: 0 0 12px 3px rgba(201,168,76,0.7); top: auto; bottom: -4px; }

  .hero-particle { position: absolute; border-radius: 50%; pointer-events: none; animation: particle-float linear infinite; }
  .hero-particle:nth-child(1) { width:4px; height:4px; background:rgba(139,92,246,0.7); left:15%; top:20%; animation-duration:7s; animation-delay:0s; }
  .hero-particle:nth-child(2) { width:3px; height:3px; background:rgba(201,168,76,0.6); left:80%; top:60%; animation-duration:9s; animation-delay:1s; }
  .hero-particle:nth-child(3) { width:5px; height:5px; background:rgba(139,92,246,0.4); left:60%; top:15%; animation-duration:11s; animation-delay:2s; }
  .hero-particle:nth-child(4) { width:3px; height:3px; background:rgba(255,255,255,0.3); left:30%; top:75%; animation-duration:8s; animation-delay:3s; }
  .hero-particle:nth-child(5) { width:4px; height:4px; background:rgba(201,168,76,0.5); left:90%; top:30%; animation-duration:10s; animation-delay:0.5s; }
  @keyframes particle-float {
    0%   { transform: translateY(0) scale(1); opacity: 0; }
    10%  { opacity: 1; } 90% { opacity: 1; }
    100% { transform: translateY(-80px) scale(0.5); opacity: 0; }
  }

  .hero-blob { position: absolute; border-radius: 50%; filter: blur(80px); pointer-events: none; }
  .hero-blob-1 { width:500px; height:500px; background:rgba(108,63,197,0.35); top:-100px; right:-100px; animation:drift 10s ease-in-out infinite alternate; }
  .hero-blob-2 { width:360px; height:360px; background:rgba(139,92,246,0.2); bottom:-80px; left:-60px; animation:drift 13s ease-in-out infinite alternate-reverse; }
  @keyframes drift { from { transform:translate(0,0) scale(1); } to { transform:translate(30px,20px) scale(1.08); } }

  .hero-content {
    position: relative; z-index: 10;
    display: flex; flex-direction: row; align-items: center;
    gap: 80px; max-width: 1200px; width: 100%;
    padding: 120px 60px 80px 80px;
  }

  .hero-image-wrap { flex-shrink: 0; position: relative; }

  .hero-image-glow {
    position: absolute; inset: -24px;
    background: radial-gradient(circle, rgba(139,92,246,0.5) 0%, transparent 70%);
    border-radius: 50%; animation: glow-pulse 3s ease-in-out infinite;
  }
  @keyframes glow-pulse { 0%,100%{opacity:0.5;transform:scale(1);} 50%{opacity:1;transform:scale(1.06);} }

  .hero-image-3d {
    transform-style: preserve-3d; transition: transform 0.1s ease;
    cursor: pointer; position: relative;
  }
  .hero-image-3d::after {
    content: ''; position: absolute; inset: 0; border-radius: 24px;
    background: linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.08) 40%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0.08) 60%, rgba(255,255,255,0) 100%);
    background-size: 200% 200%; background-position: 200% 200%;
    z-index: 3; pointer-events: none; opacity: 0;
    transition: opacity 0.3s ease, background-position 0.4s ease;
  }
  .hero-image-3d:hover::after { opacity: 1; background-position: -20% -20%; }
  .hero-image-3d:hover .hero-image {
    box-shadow: 0 50px 100px rgba(0,0,0,0.7), 0 0 0 1px rgba(139,92,246,0.5),
      -20px 20px 60px rgba(108,63,197,0.4), 20px -10px 40px rgba(201,168,76,0.15);
  }
  .hero-image-3d::before {
    content: ''; position: absolute; inset: -2px; border-radius: 26px;
    background: linear-gradient(135deg, var(--purple-light), var(--gold), var(--purple));
    z-index: 1; opacity: 0; transition: opacity 0.3s ease;
  }
  .hero-image-3d:hover::before { opacity: 1; }

  /* ── UPDATED: larger square image ── */
  .hero-image {
    width: 380px; height: 380px; object-fit: cover; border-radius: 24px;
    position: relative; z-index: 2;
    box-shadow: 0 30px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(139,92,246,0.3), 20px 20px 60px rgba(0,0,0,0.3);
    transition: box-shadow 0.3s ease;
  }

  .hero-image-reflection {
    position: absolute; bottom: -20px; left: 10%; right: 10%; height: 30px;
    background: radial-gradient(ellipse, rgba(139,92,246,0.4) 0%, transparent 70%);
    filter: blur(8px); animation: reflection-pulse 3s ease-in-out infinite;
  }
  @keyframes reflection-pulse { 0%,100%{opacity:0.5;transform:scaleX(0.9);} 50%{opacity:0.9;transform:scaleX(1.05);} }

  .hero-text { flex: 1; padding-left: 40px; }

  .hero-eyebrow {
    font-family: 'Outfit', sans-serif;
    font-size: 11px; font-weight: 600; letter-spacing: 0.25em; text-transform: uppercase;
    color: var(--gold); margin-bottom: 16px;
    opacity: 0; animation: slideUp 0.8s ease forwards 0.3s;
  }

  .hero-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(54px,7vw,82px); font-weight: 600; line-height: 1;
    color: white; margin: 0 0 8px;
    opacity: 0; animation: slideUp 0.8s ease forwards 0.5s;
  }

  .hero-title-accent {
    display: block; font-family: 'Outfit', sans-serif;
    font-size: clamp(14px,1.8vw,18px); font-weight: 400;
    background: linear-gradient(90deg, var(--purple-light), #A78BFA);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text; margin-top: 6px;
    letter-spacing: 0.08em; text-transform: uppercase;
  }

  .hero-divider {
    width: 60px; height: 2px;
    background: linear-gradient(90deg, var(--purple-light), var(--gold));
    margin: 24px 0;
    opacity: 0; animation: slideUp 0.8s ease forwards 0.7s;
  }

  .hero-desc {
    font-family: 'Outfit', sans-serif;
    font-size: 17px; line-height: 1.78; font-weight: 300;
    color: rgba(255,255,255,0.7); max-width: 480px;
    opacity: 0; animation: slideUp 0.8s ease forwards 0.9s;
  }

  .back-btn-wrap {
    margin-top: 28px;
    opacity: 0; animation: slideUp 0.8s ease forwards 1.1s;
  }
  .back-products-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 22px; border-radius: 100px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(139,92,246,0.4);
    color: rgba(255,255,255,0.75);
    font-family: 'Outfit', sans-serif;
    font-size: 0.78rem; font-weight: 500;
    letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, color 0.3s, transform 0.3s;
  }
  .back-products-btn:hover {
    background: rgba(139,92,246,0.18);
    border-color: rgba(139,92,246,0.7);
    color: #A78BFA;
    transform: translateX(-4px);
  }
  .site-link-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 22px; border-radius: 100px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(201,168,76,0.45);
    color: var(--gold);
    font-family: 'Outfit', sans-serif;
    font-size: 0.78rem; font-weight: 500;
    letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, color 0.3s, transform 0.3s;
  }
  .site-link-btn:hover {
    background: rgba(201,168,76,0.18);
    border-color: var(--gold);
    color: #fff;
    transform: translateX(-4px);
  }

  @keyframes slideUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }

  /* ── LANGUAGE VIDEOS ── */
  .lang-videos-section {
    background: linear-gradient(180deg, #120B2E 0%, #1E1250 100%);
    padding: 100px 40px; position: relative; overflow: hidden;
  }
  .lang-videos-section::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, var(--purple-light), transparent);
  }

  .lang-videos-inner { max-width: 1100px; margin: 0 auto; text-align: center; }

  .section-label {
    font-family: 'Outfit', sans-serif;
    font-size: 11px; font-weight: 600; letter-spacing: 0.25em; text-transform: uppercase;
    color: rgba(201,168,76,0.85); margin-bottom: 12px;
  }
  .section-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(30px,4vw,44px); font-weight: 500;
    color: white; margin: 0 0 56px; line-height: 1.2;
  }

  .lang-video-grid { display: grid; grid-template-columns: repeat(5,1fr); gap: 16px; justify-items: center; }
  @media (max-width: 1024px) { .lang-video-grid { grid-template-columns: repeat(3,1fr); } }
  @media (max-width: 600px)  { .lang-video-grid { grid-template-columns: repeat(2,1fr); } }

  .lang-video-card {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(139,92,246,0.25);
    border-radius: 16px; width: 100%; cursor: pointer;
    opacity: 0; transform: scale(0.92) translateY(20px);
    transition: opacity 0.6s ease, transform 0.6s ease, background 0.3s, border-color 0.3s, box-shadow 0.3s;
    display: block; overflow: hidden;
  }
  .lang-video-card.visible { opacity: 1; transform: scale(1) translateY(0); }
  .lang-video-card:hover {
    background: rgba(139,92,246,0.14); border-color: rgba(139,92,246,0.6);
    transform: translateY(-6px) scale(1.03); box-shadow: 0 20px 48px rgba(108,63,197,0.3);
  }

  .lang-video-thumb {
    position: relative; width: 100%; aspect-ratio: 16/9; border-radius: 0;
    overflow: hidden; margin-bottom: 0;
    background: rgba(108,63,197,0.2);
    display: flex; align-items: center; justify-content: center;
  }
  .lang-flag-bg { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 56px; opacity: 0.18; pointer-events: none; }
  .lang-play-overlay {
    position: absolute; inset: 0; background: rgba(18,11,46,0.45);
    display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 10px;
    transition: background 0.3s;
  }
  .lang-video-card:hover .lang-play-overlay { background: rgba(108,63,197,0.5); }
  .lang-play-btn {
    width: 52px; height: 52px; background: rgba(255,255,255,0.95); border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; padding-left: 4px; color: var(--purple);
    box-shadow: 0 4px 20px rgba(108,63,197,0.4);
    transition: transform 0.3s, box-shadow 0.3s; flex-shrink: 0;
  }
  .lang-video-card:hover .lang-play-btn { transform: scale(1.15); box-shadow: 0 8px 30px rgba(108,63,197,0.6); }
  .lang-yt-badge {
    background: #FF0000; color: white; font-family: 'Outfit', sans-serif;
    font-size: 9px; font-weight: 700; letter-spacing: 0.08em;
    padding: 3px 8px; border-radius: 4px; text-transform: uppercase;
  }

  .lang-card-body { padding: 12px 14px 14px; text-align: left; }
  .lang-name { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 14px; color: white; margin-bottom: 3px; letter-spacing: 0.02em; }
  .lang-topic { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(201,168,76,0.75); font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; margin-bottom: 2px; line-height: 1.4; }
  .lang-watch { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(255,255,255,0.35); margin-top: 5px; display: flex; align-items: center; gap: 4px; }

  /* ── VIDEO MODAL ── */
  .vid-modal-backdrop {
    position: fixed; inset: 0; background: rgba(10,6,30,0.88); z-index: 1000;
    display: flex; align-items: center; justify-content: center; padding: 24px;
    animation: modal-fade-in 0.25s ease;
  }
  @keyframes modal-fade-in { from{opacity:0;} to{opacity:1;} }
  .vid-modal-box {
    position: relative; width: 100%; max-width: 780px;
    background: #120B2E; border-radius: 24px;
    border: 1px solid rgba(139,92,246,0.35);
    box-shadow: 0 40px 120px rgba(0,0,0,0.8), 0 0 0 1px rgba(139,92,246,0.2);
    overflow: hidden; animation: modal-slide-up 0.3s cubic-bezier(0.34,1.56,0.64,1);
  }
  @keyframes modal-slide-up { from{opacity:0;transform:scale(0.88) translateY(32px);} to{opacity:1;transform:scale(1) translateY(0);} }
  .vid-modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 20px 14px; border-bottom: 1px solid rgba(139,92,246,0.15);
  }
  .vid-modal-title { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 15px; color: white; display: flex; align-items: center; gap: 8px; }
  .vid-modal-lang-badge {
    background: linear-gradient(135deg, var(--purple), var(--purple-light));
    color: white; font-family: 'Outfit', sans-serif; font-size: 10px; font-weight: 600;
    letter-spacing: 0.1em; text-transform: uppercase; padding: 3px 10px; border-radius: 100px;
  }
  .vid-modal-close {
    width: 32px; height: 32px; background: rgba(255,255,255,0.08); border: none;
    border-radius: 50%; color: rgba(255,255,255,0.7); font-size: 18px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.2s, color 0.2s; flex-shrink: 0;
  }
  .vid-modal-close:hover { background: rgba(239,68,68,0.25); color: white; }
  .vid-modal-frame-wrap { position: relative; width: 100%; aspect-ratio: 16/9; background: #000; }
  .vid-modal-frame-wrap iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: none; }
  .vid-modal-footer { padding: 14px 20px; display: flex; align-items: center; justify-content: space-between; gap: 10px; }
  .vid-modal-yt-link {
    display: flex; align-items: center; gap: 6px;
    font-family: 'Outfit', sans-serif; font-size: 12px; font-weight: 600;
    color: rgba(255,255,255,0.55); text-decoration: none; transition: color 0.2s;
  }
  .vid-modal-yt-link:hover { color: #FF4444; }
  .vid-modal-topic { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(201,168,76,0.7); letter-spacing: 0.06em; text-transform: uppercase; }

  @media (max-width: 768px) {
    .hero-content { flex-direction: column; gap: 40px; padding: 100px 24px 60px; text-align: center; }
    .hero-divider { margin: 24px auto; }
    .hero-desc { margin: 0 auto; }
    .hero-text { padding-left: 0; }
  }
    @media (max-width: 480px) {
    .hero-content { padding: 90px 16px 48px; gap: 32px; }
    .hero-image { width: 240px; height: 240px; border-radius: 18px; }
    .hero-title { font-size: clamp(44px,12vw,64px); }
    .hero-title-accent { font-size: 13px; }
    .hero-desc { font-size: 14px; }
    .back-products-btn , .site-link-btn{ font-size: 0.7rem; padding: 9px 16px; }
    .hero-action-section { padding: 48px 16px; }
    .hero-action-video-col { max-width: 100%; }
    .hero-action-yt-link { padding: 10px 18px; font-size: 0.72rem; }
    .hero-modal-backdrop { padding: 12px; }
    .hero-modal-box { border-radius: 12px; }
    .hero-modal-title { font-size: 13px; }
    .hero-modal-badge { font-size: 9px; padding: 2px 8px; }
    .hero-modal-close { width: 28px; height: 28px; font-size: 15px; }
  }
 
`;

const langVideos = [
   { lang: 'Hindi',     flag: '🇮🇳', youtubeId: '8htoz6eum6U', color: 'linear-gradient(135deg, rgba(139,92,246,0.45) 0%, rgba(60,20,150,0.6) 100%)',   accent: '#8b5cf6' },
  { lang: 'Kannada',   flag: '🇮🇳', youtubeId: 'p_hW-Zp_wh0', color: 'linear-gradient(135deg, rgba(251,146,60,0.35) 0%, rgba(120,53,15,0.6) 100%)',   accent: '#fb923c' },
  { lang: 'Tamil',     flag: '🇮🇳', youtubeId: 'rcU03PbAR8I', color: 'linear-gradient(135deg, rgba(234,179,8,0.35) 0%, rgba(113,63,18,0.6) 100%)',    accent: '#eab308' },
  { lang: 'Telugu',    flag: '🇮🇳', youtubeId: 'a6kcB-4fFa0', color: 'linear-gradient(135deg, rgba(239,68,68,0.35) 0%, rgba(127,29,29,0.6) 100%)',    accent: '#ef4444' },
  { lang: 'Malayalam', flag: '🇮🇳', youtubeId: 'lFJP747cVd4', color: 'linear-gradient(135deg, rgba(34,197,94,0.35) 0%, rgba(16,80,40,0.6) 100%)',    accent: '#22c55e' },
];

const langVideoStart = { 'lFJP747cVd4': 0, 'p_hW-Zp_wh0': 2, 'rcU03PbAR8I': 11, 'a6kcB-4fFa0': 10, '8htoz6eum6U': 8 };

function AnimatedSection({ children }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{ opacity:visible?1:0, transform:visible?'none':'translateY(28px)', transition:'opacity 0.7s ease, transform 0.7s ease' }}>
      {children}
    </div>
  );
}

const iPulse = () => {
  const langCardRefs = useRef(langVideos.map(() => React.createRef()));
  const [langCardsVisible, setLangCardsVisible] = useState(langVideos.map(() => false));
  const [activeVideo, setActiveVideo] = useState(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setActiveVideo(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleMouseMove = (e) => {
    const el = imageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const rotateY = ((x - cx) / cx) * 18;
    const rotateX = -((y - cy) / cy) * 18;
    el.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.06,1.06,1.06)`;
  };

  const handleMouseLeave = () => {
    if (imageRef.current) imageRef.current.style.transform = `perspective(800px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)`;
  };

  useEffect(() => {
    const obs = [];
    langCardRefs.current.forEach((ref, i) => {
      const o = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) setLangCardsVisible(p => { const n=[...p]; n[i]=true; return n; });
      }, { threshold: 0.1 });
      if (ref.current) o.observe(ref.current);
      obs.push(o);
    });
    return () => obs.forEach(o => o.disconnect());
  }, []);

  return (
    <>
      <CustomCursor />
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="ipulse-root min-h-screen flex flex-col">
        <Header />

        {/* ── Hero ── */}
        <section className="hero-section">
          <div className="hero-grid"><div className="hero-grid-inner" /></div>
          <div className="hero-particle" /><div className="hero-particle" /><div className="hero-particle" />
          <div className="hero-particle" /><div className="hero-particle" />
          <div className="hero-blob hero-blob-1" />
          <div className="hero-blob hero-blob-2" />

          <div className="hero-content">
            <div className="hero-image-wrap">
              <div className="hero-image-glow" />
              <div className="hero-orbit" />
              <div className="hero-orbit hero-orbit-2" />
              <div className="hero-image-3d" ref={imageRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
                <img src="/assets/product_ipulse.jpeg" alt="iPulse" className="hero-image" />
              </div>
              <div className="hero-image-reflection" />
            </div>

            <div className="hero-text">
              <p className="hero-eyebrow">Cardiovascular Wellness</p>
              <h1 className="hero-title">
                iPulse
                <span className="hero-title-accent">Product Description</span>
              </h1>
              <div className="hero-divider" />
              <p className="hero-desc">
                iPulse is a rich fusion of fruit flavonoids, polyphenols, and tannins formulated to support cardiovascular health, metabolic wellness, and natural energy. Experience relief from fatigue, improved biomarkers, and anti-inflammatory benefits with iPulse wellness supplement.
              </p>
              <div className="back-btn-wrap" style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
                <a href="/products" className="back-products-btn">← Back to Products</a>
                <a href="https://vivaipulse.com/" target="_blank" rel="noopener noreferrer" className="site-link-btn">
                  Visit iPulse Site ↗
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── Language Videos ── */}
        <section className="lang-videos-section">
          <div className="lang-videos-inner">
            <AnimatedSection>
              <p className="section-label">Watch &amp; Learn</p>
              <h2 className="section-title">iPulse in Your Language</h2>
            </AnimatedSection>

            <div className="lang-video-grid">
              {langVideos.map((v, i) => (
                <div
                  key={i}
                  ref={langCardRefs.current[i]}
                  className={`lang-video-card ${langCardsVisible[i] ? 'visible' : ''}`}
                  style={{ transitionDelay: `${i * 110}ms` }}
                  onClick={() => setActiveVideo(v)}
                >
                  <div className="lang-video-thumb" style={{ background: v.color }}>
                    <img
                      src={`https://i.ytimg.com/vi/${v.youtubeId}/hqdefault.jpg`}
                      alt={v.lang}
                      style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', opacity:0.85 }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fb = e.target.parentNode.querySelector('.lang-thumb-fallback');
                        if (fb) fb.style.display = 'flex';
                      }}
                    />
                    <div className="lang-thumb-fallback" style={{ display:'none', position:'absolute', inset:0, alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'10px', background:v.color }}>
                      <div style={{ width:'52px', height:'52px', borderRadius:'50%', background:'rgba(255,255,255,0.12)', border:`2px solid ${v.accent}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'22px' }}>{v.flag}</div>
                      <span style={{ fontSize:'12px', fontWeight:700, color:v.accent, letterSpacing:'0.08em', textTransform:'uppercase' }}>{v.lang}</span>
                    </div>
                    <div className="lang-flag-bg">{v.flag}</div>
                    <div className="lang-play-overlay">
                      <div className="lang-play-btn">▶</div>
                      <span className="lang-yt-badge">YouTube</span>
                    </div>
                  </div>
                  <div className="lang-card-body">
                    <div className="lang-name">{v.lang}</div>
                    <div className="lang-topic">Cardiac Health · Antioxidant · Immune Booster</div>
                    <div className="lang-watch"><span>▶</span> Watch Video</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Video Modal ── */}
        {activeVideo && (
          <div className="vid-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setActiveVideo(null); }}>
            <div className="vid-modal-box">
              <div className="vid-modal-header">
                <div className="vid-modal-title">
                  <span>iPulse</span>
                  <span className="vid-modal-lang-badge">{activeVideo.lang}</span>
                </div>
                <button className="vid-modal-close" onClick={() => setActiveVideo(null)}>✕</button>
              </div>
              <div className="vid-modal-frame-wrap">
                <iframe
                  src={`https://www.youtube.com/embed/${activeVideo.youtubeId}?autoplay=1&rel=0&modestbranding=1&start=${langVideoStart[activeVideo.youtubeId] || 0}`}
                  title={`iPulse ${activeVideo.lang}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="vid-modal-footer">
                <span className="vid-modal-topic">Cardiac Health · Immune Booster · Antioxidant</span>
                <a href={`https://www.youtube.com/watch?v=${activeVideo.youtubeId}&t=${langVideoStart[activeVideo.youtubeId] || 0}s`} target="_blank" rel="noopener noreferrer" className="vid-modal-yt-link">
                  <span style={{ fontSize:'14px' }}>▶</span> Open on YouTube
                </a>
              </div>
            </div>
          </div>
        )}
        {/* <TestimonyUpload/> */}

        <Footer />
      </div>
    </>
  );
};

export default iPulse;